// state/api/siteSelectors.js
import { createSelector } from '@reduxjs/toolkit';

// Always reuse the same empty array reference
const EMPTY_ARRAY = Object.freeze([]);

// Split inputs so they’re stable
const selectMode = (state) => state?.dataModeSlice?.mode ?? 'raw';
const selectRawData = (state) => state?.siteDataAPI?.data ?? EMPTY_ARRAY;
const selectProcessedData = (state) => state?.processedSiteDataAPI?.data ?? EMPTY_ARRAY;

// Choose which data set based on mode — returns the same array reference if state didn’t change
const rawSites = createSelector(
    [selectMode, selectRawData, selectProcessedData],
    (mode, raw, processed) => (mode === 'raw' ? raw : processed)
);

/**
 * Returns an ARRAY of distinct normalized site objects (one per locationId).
 * If there are multiple raw entries with the same locationId, the last one wins (and sets the order).
 */
export const selectNormalizedSites = createSelector([rawSites], (sites) => {
  const map = new Map();
  (sites || EMPTY_ARRAY).forEach((s) => {
    const key = String(s?.locationId ?? '');
    // delete first to move this key to the end (so the *last* occurrence defines position)
    if (map.has(key)) map.delete(key);
    map.set(key, {
      id: key,
      display: s?.locationName || key,
      locationId: key,
      locationName: s?.locationName || null,
      direction: s?.direction,
      lat: Number.isFinite(s?.lat) ? s.lat : null,
      lon: Number.isFinite(s?.lon) ? s.lon : null,
      hasCoordinates: Number.isFinite(s?.lat) && Number.isFinite(s?.lon),
      raw: s,
    });
  });
  return Array.from(map.values());
});

export const selectSiteOptions = createSelector([selectNormalizedSites], (rows) =>
    rows
    .slice()
    .sort((a, b) => a.display.localeCompare(b.display, 'sl', { sensitivity: 'base' }))
    .map((r) => ({
      value: r.locationId,
      label: r.display,
    }))
);
